// Dedicated SEO landing pages for the non-grocery verticals. Data-driven; rendered by
// VerticalLanding.js. Targets low-competition vertical assortment keywords.

export const VERTICAL_PAGES = {
  electronics: {
    id: "electronics",
    name: "Electronics & Hardware",
    path: "/electronics",
    demo: "electronics",
    icon: "Cpu",
    heroImg: "https://images.unsplash.com/photo-1764795850248-97a5e986b242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "Modern electronics store with wall displays and product screens",
    seoTitle: "Electronics Store Assortment & Lineup Planning Software | AssortIQ",
    seoDesc:
      "AssortIQ optimizes the product lineup for electronics and hardware stores — AI demand forecasting that's price-erosion aware, model-level keep/cut/expand decisions, display-slot optimization and store clustering. Start a free trial.",
    keywords:
      "electronics store assortment planning, consumer electronics product mix optimization, electronics lineup planning software, electronics retail inventory optimization, hardware store assortment software, which electronics models to stock",
    eyebrow: "AssortIQ for Electronics & Hardware",
    h1a: "Stock the models",
    h1b: "that actually sell.",
    subhead:
      "Product cycles are short and prices erode fast. AssortIQ scores every model on velocity, margin and price decay — so you carry the right lineup, clear aging stock before it bleeds margin, and give the display slots to what moves.",
    trialNote: "No charge today · Set up in minutes · Built for electronics & hardware retail",
    problem: {
      title: "Electronics assortment is a moving target",
      points: [
        "New models launch constantly, and last quarter's hero becomes this quarter's markdown.",
        "Price erosion quietly turns aging inventory into a margin leak you don't see until it's deep.",
        "Display slots are limited — every facing given to a slow model is a sale you didn't make.",
        "Attach items (cables, mounts, warranties) are where the margin is, but they're rarely planned.",
      ],
    },
    features: [
      { icon: "Boxes", title: "Model lineup optimization", body: "Every model scored on velocity, margin and turns with a clear keep / cut / expand call — and the dollar impact behind it." },
      { icon: "LineChart", title: "Price-aware demand forecasting", body: "Statistical forecasts with confidence bands that account for product-cycle decay, so you order into demand, not into a markdown." },
      { icon: "LayoutGrid", title: "Display-slot optimization", body: "Allocate limited display slots to the models and series that earn them — and stop funding shelf space for dead SKUs." },
      { icon: "Target", title: "Attach & bundle discovery", body: "Surface the accessories, warranties and bundles that lift basket size on every device sold." },
      { icon: "Store", title: "Store clustering", body: "Group stores by real buying behavior and tailor the lineup per cluster instead of one national planogram." },
      { icon: "Bot", title: "AI merchandising copilot", body: "Ask 'which laptops should I discontinue?' or 'where am I losing margin?' and get answers grounded in your own data." },
    ],
    stats: [
      { n: "Model-level", l: "keep / cut / expand decisions" },
      { n: "Price-aware", l: "forecasts built for short cycles" },
      { n: "Same day", l: "import a CSV and get recommendations" },
    ],
    results: {
      note: "Illustrative outcomes based on the levers AssortIQ optimizes — your results depend on your data.",
      stats: [
        { n: "−24%", l: "aging / dead stock" },
        { n: "+16%", l: "accessory attach revenue" },
        { n: "+9%", l: "GMROI" },
        { n: "3.2×", l: "inventory turns" },
      ],
    },
  },

  appliances: {
    id: "appliances",
    name: "Appliances",
    path: "/appliances",
    demo: "appliances",
    icon: "WashingMachine",
    heroImg: "https://images.pexels.com/photos/3958962/pexels-photo-3958962.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600",
    heroAlt: "Modern appliance showroom with stainless steel refrigerators",
    seoTitle: "Appliance Showroom Floor Planning & Assortment Software | AssortIQ",
    seoDesc:
      "AssortIQ optimizes the appliance showroom floor: which models to display, demand forecasting for big-ticket lead times, per-showroom digital twin and ROI simulation, and attach/financing opportunity discovery. Start a free trial.",
    keywords:
      "appliance showroom floor planning, appliance retail assortment optimization, appliance dealer inventory planning, appliance floor mix software, showroom optimization software, which appliances to display",
    eyebrow: "AssortIQ for Appliance Retail",
    h1a: "Put the right models",
    h1b: "on your showroom floor.",
    subhead:
      "Floor space is expensive and big-ticket models tie up serious cash. AssortIQ tells you which models earn their spot on the floor, forecasts demand around long lead times, and simulates the revenue and margin of any floor change before you make it.",
    trialNote: "No charge today · Set up in minutes · Built for appliance showrooms & dealers",
    problem: {
      title: "Every square foot of showroom has to earn its keep",
      points: [
        "Big-ticket models tie up cash and floor space — a slow display model is an expensive mistake.",
        "Long delivery lead times punish bad forecasts with stockouts on hot models and dead stock on cold ones.",
        "Financing and attach (delivery, install, protection plans) drive margin but rarely get planned.",
        "Without per-showroom insight, you run the same floor everywhere — even when local demand differs.",
      ],
    },
    features: [
      { icon: "LayoutGrid", title: "Showroom floor optimization", body: "Score every model on margin, turns and sell-through to decide what earns a spot on the floor — and what to pull." },
      { icon: "LineChart", title: "Lead-time-aware forecasting", body: "Demand forecasts with confidence bands tuned for big-ticket lead times, so you order early enough without over-committing cash." },
      { icon: "Gauge", title: "Per-showroom Digital Twin", body: "A model of each showroom projects the revenue, margin and cash impact of a floor change before you commit inventory to it." },
      { icon: "Target", title: "Attach & financing discovery", body: "Find the delivery, install, protection-plan and financing attach that lifts ticket value on every sale." },
      { icon: "Store", title: "Showroom clustering", body: "Group showrooms by demand pattern and tailor the floor per cluster instead of one identical layout." },
      { icon: "Sparkles", title: "7-lever financial simulation", body: "Model price, mix, space and promo changes and see the projected P&L impact before anything hits the floor." },
    ],
    stats: [
      { n: "Per-showroom", l: "floor & ROI modeling" },
      { n: "Lead-time", l: "aware demand forecasts" },
      { n: "Same day", l: "import a CSV and get recommendations" },
    ],
    results: {
      note: "Illustrative outcomes based on the levers AssortIQ optimizes — your results depend on your data.",
      stats: [
        { n: "−22%", l: "slow floor models" },
        { n: "+14%", l: "attach & financing" },
        { n: "+11%", l: "margin on the floor" },
        { n: "−18%", l: "markdowns" },
      ],
    },
  },

  cellular: {
    id: "cellular",
    name: "Cellular & Mobile",
    path: "/cellular",
    demo: "cellular",
    icon: "Smartphone",
    heroImg: "https://images.unsplash.com/photo-1777073233203-314199f1f1e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "Smartphones displayed on stands in a cellular retail showroom",
    seoTitle: "Cellular Store Device Lineup & Accessory Attach Optimization | AssortIQ",
    seoDesc:
      "AssortIQ optimizes the device lineup and accessory attach rate for cellular and wireless retail — device-level keep/cut/expand, accessory attach discovery, demand forecasting and per-store/kiosk clustering. Start a free trial.",
    keywords:
      "cellular store device lineup optimization, phone accessory attach rate optimization, wireless retail assortment planning, cell phone store inventory software, accessory attach rate, which phones to stock",
    eyebrow: "AssortIQ for Cellular & Mobile",
    h1a: "Sell more devices —",
    h1b: "and the accessories with them.",
    subhead:
      "In wireless retail the margin lives in the accessory attach. AssortIQ optimizes your device lineup, surfaces the cases, chargers and add-ons that attach best to each phone, and tailors the mix per store and kiosk.",
    trialNote: "No charge today · Set up in minutes · Built for cellular stores & kiosks",
    problem: {
      title: "The device sells the store — the attach makes the money",
      points: [
        "Devices refresh fast; carry the wrong lineup and you lose the traffic that drives everything else.",
        "Accessory attach rate is where margin lives, but few stores plan which add-ons pair with which phone.",
        "Kiosk and small-store display space is tiny — every slot has to pull its weight.",
        "Plan and device mix vary by location, but most operators run one lineup everywhere.",
      ],
    },
    features: [
      { icon: "Boxes", title: "Device lineup optimization", body: "Score every device on velocity, margin and turns with a clear keep / cut / expand call and the dollar impact behind it." },
      { icon: "Target", title: "Accessory attach discovery", body: "Surface the cases, chargers, screen protectors and add-ons that attach best to each device — and lift them everywhere." },
      { icon: "LineChart", title: "Demand forecasting", body: "Statistical forecasts with confidence bands so hot devices never run out and slow ones don't clog your limited space." },
      { icon: "LayoutGrid", title: "Display-slot optimization", body: "Allocate scarce kiosk and wall slots to the devices and accessories that earn them." },
      { icon: "Store", title: "Store & kiosk clustering", body: "Group locations by real demand and tailor the device and plan mix per cluster, not one lineup for all." },
      { icon: "Bot", title: "AI merchandising copilot", body: "Ask 'which accessories should I push with this phone?' and get answers grounded in your own sales data." },
    ],
    stats: [
      { n: "Attach-rate", l: "opportunity discovery" },
      { n: "Device-level", l: "keep / cut / expand decisions" },
      { n: "Same day", l: "import a CSV and get recommendations" },
    ],
    results: {
      note: "Illustrative outcomes based on the levers AssortIQ optimizes — your results depend on your data.",
      stats: [
        { n: "+21%", l: "accessory attach rate" },
        { n: "−19%", l: "slow-moving devices" },
        { n: "+12%", l: "gross margin" },
        { n: "6.4×", l: "inventory turns" },
      ],
    },
  },

  retail: {
    id: "retail",
    name: "General Retail",
    path: "/retail",
    demo: "retail",
    icon: "Store",
    heroImg: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    heroAlt: "Interior of a modern general retail store with product displays",
    seoTitle: "Retail Assortment Optimization Software for Independents | AssortIQ",
    seoDesc:
      "AssortIQ is explainable AI assortment optimization for independent and regional retailers — department-level keep/cut/expand decisions, demand forecasting, store clustering, display-space optimization and financial simulation. Start a free trial.",
    keywords:
      "retail assortment optimization software, assortment planning for small retailers, SKU rationalization software, dead inventory reduction, retail merchandising software for independents, what products to stock",
    eyebrow: "AssortIQ for General Retail",
    h1a: "Carry what sells.",
    h1b: "Cut what doesn't.",
    subhead:
      "AssortIQ scores every item across your departments on velocity, margin, turns and stockouts — and tells you what to keep, cut and expand, with the dollar impact shown. Explainable AI for independents and regionals, no data team required.",
    trialNote: "No charge today · Set up in minutes · Built for independent & regional retail",
    problem: {
      title: "Your best-selling items are hiding behind the tail",
      points: [
        "A long tail of slow SKUs quietly ties up cash and shelf space that your winners could use.",
        "Reorders run on gut feel and last year's numbers instead of real, forward-looking demand.",
        "Every store runs the same assortment even when local demand clearly differs.",
        "You know something's underperforming — but not exactly what to cut or what to back instead.",
      ],
    },
    features: [
      { icon: "Boxes", title: "Assortment optimization", body: "Every item scored on velocity, margin and turns with a clear keep / cut / expand call and the dollar impact behind it." },
      { icon: "LineChart", title: "Demand forecasting", body: "Statistical forecasts with confidence bands, promo and seasonality lift so winners never run out and slow items don't pile up." },
      { icon: "LayoutGrid", title: "Display-space optimization", body: "Allocate shelf and display space to the items and classes that earn it — and stop funding space for dead SKUs." },
      { icon: "Target", title: "Opportunity discovery", body: "Surface gaps, add-ons and cross-sell opportunities you're leaving on the table in each department." },
      { icon: "Store", title: "Store clustering", body: "Group stores by real buying behavior and tailor the assortment per cluster instead of one plan for all." },
      { icon: "Bot", title: "AI merchandising copilot", body: "Ask 'what should I discontinue?' or 'where am I losing margin?' and get answers grounded in your own data." },
    ],
    stats: [
      { n: "Item-level", l: "keep / cut / expand decisions" },
      { n: "Explainable", l: "every call shows its reasoning" },
      { n: "Same day", l: "import a CSV and get recommendations" },
    ],
    results: {
      note: "Illustrative outcomes based on the levers AssortIQ optimizes — your results depend on your data.",
      stats: [
        { n: "−28%", l: "dead stock" },
        { n: "+15%", l: "category margin" },
        { n: "+12%", l: "GMROI" },
        { n: "+9%", l: "sell-through" },
      ],
    },
  },
};

export const getVerticalPage = (id) => VERTICAL_PAGES[id] || null;
export const VERTICAL_PAGE_LIST = Object.values(VERTICAL_PAGES);
